import { ChangeDetectorRef, Component, Inject, OnInit, Optional } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { LuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiConfiguration } from 'app/api/api-configuration';
import { PartQuoteDto, SupplierDto } from 'app/api/models';
import { SupplierService } from 'app/api/services';
import { PackCostDialogComponent, roundTo4dp } from '../pack-cost-dialog/pack-cost-dialog.component';

export interface RecordPartQuoteDialogData {
  partId: number;
  partNumber: string;
  currentCost: number;
  /** Price the user already typed somewhere else (BOM builder unit-cost cell) — seeds the
   *  form so they don't key it twice. Omitted for the manual "record a quote" triggers. */
  quotedCost?: number;
}

/**
 * Standalone (CLAUDE.md rule): this dialog opens from the parts register, V1's BOM builder,
 * V1's quote wizard AND the V2 builder-core, which live behind different lazy modules — as a
 * module-declared component its directives resolve inconsistently across those boundaries.
 */
@Component({
    selector: 'app-record-part-quote-dialog',
    templateUrl: './record-part-quote-dialog.component.html',
    standalone: true,
    imports: [
        CurrencyPipe, ReactiveFormsModule,
        MatAutocompleteModule, MatButtonModule, MatCheckboxModule, MatDatepickerModule,
        MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule, MatSelectModule,
    ],
    // The date adapter is pinned HERE, not inherited. This dialog opens from four screens on
    // three lazy modules, and SharedMaterialModule's MatNativeDateModule shadows the root
    // Luxon adapter in some of those injectors — which showed the quote date American-style
    // (7/29/2026) and made a typed 29/07/2026 parse to Invalid. Own providers = one
    // behaviour everywhere: UK display, UK typing.
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
        { provide: DateAdapter, useClass: LuxonDateAdapter },
        {
            provide: MAT_DATE_FORMATS,
            useValue: {
                parse: { dateInput: ['dd/MM/yyyy', 'd/M/yyyy', 'd/M/yy', 'yyyy-MM-dd', 'D'] },
                display: {
                    dateInput: 'dd/MM/yyyy',
                    monthYearLabel: 'LLL yyyy',
                    dateA11yLabel: 'DD',
                    monthYearA11yLabel: 'LLLL yyyy',
                },
            },
        },
    ],
})
export class RecordPartQuoteDialogComponent implements OnInit {
  public form: UntypedFormGroup;
  public suppliers: SupplierDto[] = [];
  /** What the panel currently shows: everything on focus, matches while typing. */
  public filteredSuppliers: SupplierDto[] = [];
  public saving = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: RecordPartQuoteDialogData,
    private dialogRef: MatDialogRef<RecordPartQuoteDialogComponent>,
    private dialog: MatDialog,
    private fb: UntypedFormBuilder,
    private http: HttpClient,
    private apiConfig: ApiConfiguration,
    private supplierService: SupplierService,
    private snack: MatSnackBar,
    private cdr: ChangeDetectorRef,
    @Optional() private dateAdapter: DateAdapter<unknown>,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      supplierId:           [null,                                            Validators.required],
      // min is a PENNY-QUARTER, not a penny: dividing a pack price down legitimately lands
      // below 1p (a £4.99 box of 250 is £0.0200 each) and min(0.01) would reject the very
      // conversion this dialog now performs.
      cost:                 [this.data.quotedCost ?? this.data.currentCost ?? 0, [Validators.required, Validators.min(0.0001)]],
      quoteDate:            [this.today(),                                    Validators.required],
      setAsPrimarySupplier: [false],
    });

    // Seeded so simply tabbing through the untouched cost box does not raise the pack dialog.
    this.lastAskedCost = roundTo4dp(Number(this.form.value.cost ?? 0));

    this.loadSuppliers();
  }

  /** The cost the pack question has already been answered for. */
  private lastAskedCost: number | null = null;

  /**
   * A supplier quote is where pack pricing genuinely arrives ("box of 250, £4.99"), so the
   * pack question belongs here as much as on the part form. The field is labelled "per unit"
   * and the server writes it straight onto the part, so a pack price keyed here without
   * conversion overstates that part's cost by the pack size on every BOM that uses it.
   */
  costBlurred(): void {
    const control = this.form?.controls['cost'];
    const entered = roundTo4dp(Number(control?.value ?? 0));
    if (!entered || entered === this.lastAskedCost) return;

    PackCostDialogComponent.ask(this.dialog, { enteredCost: entered, partNumber: this.data.partNumber })
      .subscribe(result => {
        // Cancelled: leave what they typed alone and ask again next time it changes.
        if (!result) return;
        this.lastAskedCost = result.unitCost;
        control?.setValue(result.unitCost);
        this.cdr.detectChanges();
      });
  }

  /** Ask the ACTIVE adapter for today rather than handing it a JS Date: routes differ on
   *  whether the Luxon or the native adapter is in scope (see the two-adapters landmine). */
  private today(): unknown {
    return this.dateAdapter?.today ? this.dateAdapter.today() : new Date();
  }

  loadSuppliers(): void {
    this.supplierService.apiSupplierGetAllGet$Json$Response().subscribe(res => {
      if (!res?.body?.data) return;
      this.suppliers = [...res.body.data].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
      this.filteredSuppliers = this.suppliers;
      this.cdr.detectChanges();
    });
  }

  /** Null event = the box was opened without typing, so show the lot. */
  filterSuppliers(event: any): void {
    const term = (event?.target?.value ?? '').toString().trim().toLowerCase();
    this.filteredSuppliers = !term
      ? this.suppliers
      : this.suppliers.filter(s => (s.name ?? '').toLowerCase().includes(term));
  }

  /** The input displays the NAME while the control stores the supplier ID. */
  supplierName = (supplierId: number | null): string =>
    this.suppliers.find(s => s.supplierId === supplierId)?.name ?? '';

  /** Leaving the box with half-typed text would leave that text sitting in the control as
   *  if it were an id; blank it so Save Quote stays correctly disabled.
   *
   *  DEFERRED: Chromium fires this blur on the option's MOUSEDOWN, before the click that commits
   *  the pick, so clearing synchronously wiped the selection the user was making (Firefox blurs
   *  after the click, hence Chrome/Edge only). A macrotask later the pick has landed, so this
   *  only clears genuinely abandoned text. Same fix as SelectSearchComponent.onBlur. */
  clearIfNotPicked(): void {
    setTimeout(() => {
      const control = this.form?.controls['supplierId'];
      if (!control?.value) return;
      if (!this.suppliers.some(s => s.supplierId === control.value)) control.setValue(null);
    });
  }

  /** Same adapter problem on the way out: the control can hold a Luxon DateTime, a JS Date
   *  or a string. Build from LOCAL parts — toISOString() on a BST midnight sends the
   *  previous day. */
  private toIsoDate(value: unknown): string | null {
    if (!value) return null;
    if (typeof value === 'string') return value.substring(0, 10) + 'T00:00:00';
    const luxon = (value as { toISODate?: () => string | null }).toISODate?.();
    if (luxon) return luxon + 'T00:00:00';
    if (value instanceof Date && !isNaN(value.getTime())) {
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T00:00:00`;
    }
    return null;
  }

  save(): void {
    if (!this.form.valid || this.saving) return;

    const v = this.form.value;
    const quoteDate = this.toIsoDate(v.quoteDate);
    if (!quoteDate) {
      this.snack.open('Enter a valid quote date as dd/mm/yyyy.', 'OK', { duration: 5000 });
      return;
    }

    this.saving = true;
    const dto: PartQuoteDto = {
      partId: this.data.partId,
      supplierId: v.supplierId,
      cost: Number(v.cost),
      quoteDate,
      setAsPrimarySupplier: v.setAsPrimarySupplier,
    };

    this.http.post<any>(`${this.apiConfig.rootUrl}/api/Part/part-quote`, dto).subscribe({
      next: (res) => {
        this.saving = false;
        if (res?.errors?.length) {
          this.snack.open('Cannot save quote - ' + res.errors.join(', '), 'OK', { duration: 8000 });
          return;
        }
        const costRaised = dto.cost > (this.data.currentCost ?? 0);
        this.snack.open(
          costRaised ? 'Quote saved. Part cost updated.' : 'Quote saved. Part cost unchanged (new quote was lower).',
          'OK',
          { duration: 4000 },
        );
        this.dialogRef.close(res?.data ?? true);
      },
      error: () => {
        this.saving = false;
        this.snack.open('Failed to save quote. Try again.', 'OK', { duration: 5000 });
      },
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }
}
